'use client'

import {
  Fragment,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react'
import { toast } from 'sonner'
import { Plus, Trash2, Pencil, Save, X, Loader2 } from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import axios from '@/lib/api/axios'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import { Badge } from '@/components/ui/shadcn/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'

const TYPE_OPTIONS = [
  { value: 'duration', label: 'duration' },
  { value: 'boolean', label: 'boolean' },
  { value: 'integer', label: 'integer' },
  { value: 'number', label: 'number' },
  { value: 'enum', label: 'enum' },
]

const KIND_OPTIONS = [
  { value: 'ai_vendor', label: 'AI Vendor' },
  { value: 'technology', label: 'Technology' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'subcontractor', label: 'Subcontractor' },
]

const KEY_RE = /^[a-zA-Z][a-zA-Z0-9_]*$/

function emptyForm() {
  return {
    key: '',
    label: '',
    type: 'duration',
    unit: '',
    enumValuesText: '',
    applicableKinds: [],
  }
}

function KindCheckboxes({ value, onChange }) {
  const toggle = k => {
    if (value.includes(k)) onChange(value.filter(x => x !== k))
    else onChange([...value, k])
  }
  return (
    <div className="flex flex-wrap gap-2">
      {KIND_OPTIONS.map(opt => {
        const selected = value.includes(opt.value)
        return (
          <button
            type="button"
            key={opt.value}
            onClick={() => toggle(opt.value)}
            className={`rounded-md border px-2 py-1 text-[11px] ${
              selected
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border bg-card text-muted-foreground hover:bg-muted'
            }`}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

/**
 * Settings → Critical Dependencies tab — parameter registry editor.
 *
 * Lists every parameter definition for this org. Seeded entries (seeded:true)
 * are read-only structurally; only the label may be edited (the migration
 * owns the rest of the schema). User-added entries can be edited or deleted
 * outright.
 */
export default function ParameterRegistry() {
  const authContext = useContext(AuthContext)
  const orgId = authContext.user?.organization?.id
  const canView = authContext.user?.isAuthorized('viewCriticalDependencies')
  const canManage = authContext.user?.isAuthorized('manageCriticalDependencies')

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState(null)
  const [busyId, setBusyId] = useState(null)

  const [addOpen, setAddOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  const [newForm, setNewForm] = useState(() => emptyForm())

  const fetchItems = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    setError(null)
    try {
      const { data } = await axios(
        `/api/admin/organization/${orgId}/parameter-definitions`,
        { method: 'GET' },
        authContext,
      )
      setItems(data.items || [])
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load registry')
    } finally {
      setLoading(false)
    }
  }, [authContext, orgId])

  useEffect(() => {
    if (canView) fetchItems()
  }, [fetchItems, canView])

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      // Seeded first, then alpha by label.
      if (a.seeded !== b.seeded) return a.seeded ? -1 : 1
      return (a.label || '').localeCompare(b.label || '')
    })
  }, [items])

  const startEdit = def => {
    setEditingId(def.id)
    setEditForm({
      label: def.label || '',
      type: def.type,
      unit: def.unit || '',
      enumValuesText: (def.enumValues || []).join(', '),
      applicableKinds: def.applicableKinds || [],
    })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditForm(null)
  }

  const handleSave = async def => {
    setBusyId(def.id)
    try {
      // Label, unit, enum values, and kinds are editable on any entry; type
      // only on user-added (non-seeded) entries.
      const body = {
        label: editForm.label,
        unit: editForm.unit,
        applicableKinds: editForm.applicableKinds,
      }
      if (editForm.type === 'enum') {
        body.enumValues = editForm.enumValuesText
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
      }
      if (!def.seeded) {
        body.type = editForm.type
      }
      await axios(
        `/api/admin/organization/${orgId}/parameter-definitions/${def.id}`,
        { method: 'PUT', data: body },
        authContext,
      )
      toast.success('Saved')
      cancelEdit()
      fetchItems()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save')
    } finally {
      setBusyId(null)
    }
  }

  const handleDelete = async def => {
    setBusyId(def.id)
    try {
      await axios(
        `/api/admin/organization/${orgId}/parameter-definitions/${def.id}`,
        { method: 'DELETE' },
        authContext,
      )
      toast.success('Deleted')
      fetchItems()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete')
    } finally {
      setBusyId(null)
    }
  }

  const handleAdd = async () => {
    if (!KEY_RE.test(newForm.key)) {
      toast.error(
        'Key must match ^[a-zA-Z][a-zA-Z0-9_]*$ (letters, digits, underscores; starts with a letter)',
      )
      return
    }
    if (!newForm.label) {
      toast.error('Label is required')
      return
    }
    setAdding(true)
    try {
      const body = {
        key: newForm.key,
        label: newForm.label,
        type: newForm.type,
        unit: newForm.unit || undefined,
        applicableKinds: newForm.applicableKinds,
      }
      if (newForm.type === 'enum') {
        body.enumValues = newForm.enumValuesText
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
      }
      await axios(
        `/api/admin/organization/${orgId}/parameter-definitions`,
        { method: 'POST', data: body },
        authContext,
      )
      toast.success('Parameter added')
      setNewForm(emptyForm())
      setAddOpen(false)
      fetchItems()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add')
    } finally {
      setAdding(false)
    }
  }

  if (!canView) {
    return (
      <p className="text-sm text-muted-foreground">
        You don&apos;t have permission to view this registry.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold">
            Critical Dependency Parameters
          </h2>
          <p className="text-xs text-muted-foreground">
            Parameters defined here can be used as baselines on a dependency, as
            policy thresholds, and as trigger field mappings. Standard (seeded)
            entries can be relabelled but not deleted.
          </p>
        </div>
        {canManage && (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus />
            Add parameter
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : (
        <div className="rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Key</th>
                <th className="px-3 py-2 text-left font-medium">Label</th>
                <th className="px-3 py-2 text-left font-medium">Type</th>
                <th className="px-3 py-2 text-left font-medium">Unit</th>
                <th className="px-3 py-2 text-left font-medium">
                  Applicable kinds
                </th>
                <th className="px-3 py-2 text-left font-medium">Source</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sortedItems.map(def => {
                const isEditing = editingId === def.id
                return (
                  <Fragment key={def.id}>
                    <tr className="text-xs">
                      <td className="px-3 py-2 font-mono">{def.key}</td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Input
                            className="h-8 text-xs"
                            value={editForm.label}
                            onChange={e =>
                              setEditForm(p => ({
                                ...p,
                                label: e.target.value,
                              }))
                            }
                          />
                        ) : (
                          def.label
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {isEditing && !def.seeded ? (
                          <Select
                            value={editForm.type}
                            onValueChange={v =>
                              setEditForm(p => ({ ...p, type: v }))
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TYPE_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          def.type
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <Input
                            className="h-8 text-xs"
                            value={editForm.unit}
                            onChange={e =>
                              setEditForm(p => ({
                                ...p,
                                unit: e.target.value,
                              }))
                            }
                          />
                        ) : (
                          def.unit || '—'
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {isEditing ? (
                          <KindCheckboxes
                            value={editForm.applicableKinds}
                            onChange={v =>
                              setEditForm(p => ({
                                ...p,
                                applicableKinds: v,
                              }))
                            }
                          />
                        ) : def.applicableKinds?.length ? (
                          <span className="capitalize">
                            {def.applicableKinds.join(', ')}
                          </span>
                        ) : (
                          'all'
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {def.seeded ? (
                          <Badge variant="outline" className="text-[10px]">
                            standard
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px]">
                            custom
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {canManage &&
                          (isEditing ? (
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={cancelEdit}
                                disabled={busyId === def.id}
                              >
                                <X />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleSave(def)}
                                disabled={busyId === def.id}
                              >
                                <Save />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => startEdit(def)}
                              >
                                <Pencil />
                              </Button>
                              {!def.seeded && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDelete(def)}
                                  disabled={busyId === def.id}
                                >
                                  <Trash2 />
                                </Button>
                              )}
                            </div>
                          ))}
                      </td>
                    </tr>
                    {isEditing && editForm.type === 'enum' && (
                      <tr className="bg-muted/20">
                        <td colSpan={7} className="px-3 py-2">
                          <div className="flex flex-col gap-1.5">
                            <Label className="text-xs">
                              Enum values (comma-separated)
                            </Label>
                            <Input
                              className="h-8 text-xs"
                              value={editForm.enumValuesText}
                              onChange={e =>
                                setEditForm(p => ({
                                  ...p,
                                  enumValuesText: e.target.value,
                                }))
                              }
                              placeholder="green, amber, red"
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
              {sortedItems.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-6 text-center text-xs text-muted-foreground"
                  >
                    No parameter definitions yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {addOpen && canManage && (
        <div className="rounded-md border border-dashed border-border bg-muted/30 p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-medium">Add parameter</p>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setAddOpen(false)
                setNewForm(emptyForm())
              }}
            >
              <X />
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">Key</Label>
              <Input
                value={newForm.key}
                onChange={e => setNewForm(p => ({ ...p, key: e.target.value }))}
                placeholder="e.g. rto_seconds"
                className="h-8 text-xs font-mono"
              />
              <p className="text-[11px] text-muted-foreground">
                Letters, digits, underscores; starts with a letter.
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">Label</Label>
              <Input
                value={newForm.label}
                onChange={e =>
                  setNewForm(p => ({ ...p, label: e.target.value }))
                }
                placeholder="e.g. Recovery Time Objective"
                className="h-8 text-xs"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">Type</Label>
              <Select
                value={newForm.type}
                onValueChange={v => setNewForm(p => ({ ...p, type: v }))}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">Unit (optional)</Label>
              <Input
                value={newForm.unit}
                onChange={e =>
                  setNewForm(p => ({ ...p, unit: e.target.value }))
                }
                placeholder="e.g. seconds, %, USD"
                className="h-8 text-xs"
              />
            </div>
            {newForm.type === 'enum' && (
              <div className="md:col-span-2 flex flex-col gap-1.5">
                <Label className="text-xs">Enum values (comma-separated)</Label>
                <Input
                  value={newForm.enumValuesText}
                  onChange={e =>
                    setNewForm(p => ({
                      ...p,
                      enumValuesText: e.target.value,
                    }))
                  }
                  placeholder="green, amber, red"
                  className="h-8 text-xs"
                />
              </div>
            )}
            <div className="md:col-span-2 flex flex-col gap-1.5">
              <Label className="text-xs">
                Applicable kinds (none = applies to all)
              </Label>
              <KindCheckboxes
                value={newForm.applicableKinds}
                onChange={v => setNewForm(p => ({ ...p, applicableKinds: v }))}
              />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button size="sm" onClick={handleAdd} disabled={adding}>
              <Save />
              {adding ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
